import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { Suspense, useMemo } from "react";
import {
  PreloadedQuery,
  usePreloadedQuery,
  useSubscription,
} from "react-relay";
import { GraphQLSubscriptionConfig } from "relay-runtime";

import { Loader } from "@whoosh/design-system/src/components/display";
import {
  Box,
  VStack,
  Text,
} from "@whoosh/design-system/src/components/primitive";

import { AgendaWaitlistBody } from "./AgendaWaitlistBody";
import { AgendaWaitlistRendererQuery } from "./__generated__/AgendaWaitlistRendererQuery.graphql";
import { AgendaWaitlistRendererQueueSlotSubscription } from "./__generated__/AgendaWaitlistRendererQueueSlotSubscription.graphql";

type AgendaWaitlistRendererProps = {
  queryRef: PreloadedQuery<AgendaWaitlistRendererQuery>;
  onJoinWaitlist?: (queueSlotId: string, requestedTime: string | null) => void;
};

export const AgendaWaitlistRenderer = ({
  queryRef,
  onJoinWaitlist,
}: AgendaWaitlistRendererProps) => {
  const data = usePreloadedQuery(AgendaWaitlistRendererQueryString, queryRef);
  const hasWaitlistRestrictions =
    !!data?.node?.agendas?.edges?.[0]?.node?.waitlistRestrictions?.edges
      ?.length;

  const agenda = data.node?.agendas?.edges
    ? data.node.agendas.edges[0]?.node
    : null;

  const Content = () => {
    return (
      <Box height="100%">
        <Suspense
          fallback={
            <Box pb={6}>
              <Loader />
            </Box>
          }
        >
          {queryRef ? (
            <AgendaWaitlistBody
              fragmentRef={data.node ?? null}
              onJoinWaitlist={onJoinWaitlist}
            />
          ) : null}
        </Suspense>
      </Box>
    );
  };

  const hasBookings =
    agenda?.queueSlot.bookings?.edges &&
    agenda?.queueSlot.bookings?.edges?.length > 0;

  return (
    <>
      {agenda?.id ? <QueueSlotSubscription agendaId={agenda.id} /> : null}
      {!hasBookings && !hasWaitlistRestrictions ? null : (
        <VStack gap={6}>
          <Text variant="mainHeading">{t`Waitlist`}</Text>
          <Content />
        </VStack>
      )}
    </>
  );
};

const QueueSlotSubscription = ({ agendaId }: { agendaId: string }) => {
  const config: GraphQLSubscriptionConfig<AgendaWaitlistRendererQueueSlotSubscription> =
    useMemo(() => {
      return {
        variables: { agendaId },
        subscription: AgendaWaitlistRendererQueueSlotSubscriptionString,
      };
    }, [agendaId]);

  useSubscription(config);

  return null;
};

export const AgendaWaitlistRendererQueryString = graphql`
  query AgendaWaitlistRendererQuery(
    $facilityId: ID!
    $date: Date!
    $courseIds: [ID!]
  ) {
    node(id: $facilityId) {
      ... on Facility {
        ...AgendaWaitlistBodyFragment
        agendas(dateGte: $date, dateLte: $date) {
          edges {
            node {
              id
              queueSlot {
                id
                bookings(
                  first: 1000
                  orderBy: { field: REQUESTED_DATE_TIME, direction: ASC }
                ) @connection(key: "queueSlot_bookings") {
                  edges {
                    node {
                      id
                    }
                  }
                }
              }
              waitlistRestrictions: bookingRules(
                type: [WAITLIST]
                courseIds: $courseIds
              ) {
                edges {
                  liftAt
                  node {
                    id
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

const AgendaWaitlistRendererQueueSlotSubscriptionString = graphql`
  subscription AgendaWaitlistRendererQueueSlotSubscription($agendaId: ID!) {
    queueSlotChanged(agendaIds: [$agendaId]) {
      slots {
        ... on QueueSlot {
          id
          queueCount: bookings {
            count
          }
          bookings(
            first: 1000
            orderBy: { field: REQUESTED_DATE_TIME, direction: ASC }
          ) @connection(key: "queueSlot_bookings") {
            edges {
              node {
                id
                ... on StandardBooking {
                  request {
                    time
                  }
                }

                ...AgendaWaitlistBooking_Booking
              }
            }
          }
        }
      }
    }
  }
`;
